import * as React from "react";
import PropTypes from "prop-types";
import { 
  makeStyles, 
  Button, 
  Text, 
  Spinner,
  MessageBar,
  Skeleton,
  SkeletonItem,
  Input,
  Field,
  Dropdown,
  Option,
  Textarea,
  Tab,
  TabList
} from "@fluentui/react-components";
import { 
  DocumentText24Regular, 
  Edit24Regular, 
  Copy24Regular,
  CheckmarkCircle24Regular,
  Send24Regular,
  Add24Regular,
  Mail24Regular
} from "@fluentui/react-icons";
import { emailProcessor } from "../../services/emailProcessor";
import { streamlineAPI } from "../../services/streamlineAPI";

// Add global styles for fonts
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  
  /* Avenir font family fallback */
  * {
    font-family: 'Avenir', 'Avenir Next', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif !important;
  }
  
  /* Remove animation styles - keeping it simple */
  .streamline-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    transition: all 0.2s ease;
  }
  
  .streamline-card:hover {
    border-color: #FFB500;
    box-shadow: 0 4px 16px rgba(255, 181, 0, 0.12);
  }
  
  .streamline-card.featured {
    border-color: #FFB500;
    box-shadow: 0 2px 12px rgba(255, 181, 0, 0.08);
  }
`;

// Inject global styles only once
if (typeof document !== 'undefined' && !document.getElementById('streamline-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'streamline-styles';
  styleElement.textContent = globalStyles;
  document.head.appendChild(styleElement);
}

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    padding: "16px",
    backgroundColor: "#f8f9fa",
    fontFamily: "'Avenir', 'Avenir Next', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#374151",
  },
  tabContainer: {
    marginBottom: "24px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    padding: "40px 20px",
  },
  resultsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  resultCard: {
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    transition: "all 0.2s ease",
    overflow: "hidden",
    "&:hover": {
      boxShadow: "0 4px 16px rgba(255, 181, 0, 0.12)",
      borderColor: "#FFB500",
    },
  },
  resultHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px 12px 20px",
    borderBottom: "1px solid #f3f4f6",
    backgroundColor: "#fafbfc",
  },
  resultContent: {
    padding: "20px",
    lineHeight: "1.6",
    fontSize: "14px",
    color: "#374151",
    fontFamily: "'Avenir', 'Avenir Next', 'Inter', sans-serif",
  },
  resultTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#374151",
    fontWeight: "600",
    fontSize: "14px",
    fontFamily: "'Avenir', 'Avenir Next', 'Inter', sans-serif",
  },
  resultBadge: {
    backgroundColor: "#f3f4f6",
    color: "#0B111A",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600",
    fontFamily: "'Avenir', 'Avenir Next', 'Inter', sans-serif",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    border: "1px solid #e5e7eb",
  },
  contentText: {
    margin: "0",
    lineHeight: "1.7",
    fontSize: "14px",
    color: "#374151",
    fontFamily: "'Avenir', 'Avenir Next', 'Inter', sans-serif",
    "& strong": {
      fontWeight: "600",
      color: "#0B111A",
      backgroundColor: "#fff3cd",
      padding: "2px 4px",
      borderRadius: "3px",
    },
    "& em": {
      fontStyle: "italic",
      color: "#6b7280",
    },
    "& .highlight": {
      backgroundColor: "#fff3cd",
      color: "#0B111A",
      fontWeight: "600",
      padding: "2px 4px",
      borderRadius: "3px",
    },
  },
  bulletPoint: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "8px",
    "&:last-child": {
      marginBottom: "0",
    },
  },
  bulletIcon: {
    width: "6px",
    height: "6px",
    backgroundColor: "#FFB500",
    borderRadius: "50%",
    marginTop: "8px",
    flexShrink: 0,
  },
  copyButton: {
    minWidth: "80px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "500",
    fontFamily: "Avenir, sans-serif",
    "&:hover": {
      borderColor: "#FFB500",
    },
  },
  successIcon: {
    color: "#10b981",
  },
  shimmerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    marginTop: "24px",
  },
  shimmerCard: {
    padding: "20px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "2px solid #FFB500",
    boxShadow: "0 4px 12px rgba(255, 181, 0, 0.15)",
  },
  shimmerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  fadeIn: {
    animationName: {
      "0%": {
        opacity: "0",
        transform: "translateY(16px)",
      },
      "100%": {
        opacity: "1",
        transform: "translateY(0)",
      },
    },
    animationDuration: "0.6s",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
  },
  instructionsContainer: {
    marginTop: "24px",
    padding: "24px",
    backgroundColor: "white",
    borderRadius: "16px",
    border: "2px solid transparent",
    backgroundImage: "linear-gradient(white, white), conic-gradient(from 0deg, #FFB500, #ffc42e, #FFB500, #e6a200, #FFB500)",
    backgroundOrigin: "border-box",
    backgroundClip: "padding-box, border-box",
    boxShadow: "0 4px 16px rgba(255, 181, 0, 0.1)",
    animation: "rotateBorder 8s linear infinite",
  },
  instructionsInput: {
    marginBottom: "16px",
    fontFamily: "'Inter', 'DM Sans', sans-serif",
  },
  instructionsLabel: {
    marginBottom: "12px",
    fontWeight: "600",
    color: "#0B111A",
    fontFamily: "'Inter', 'DM Sans', sans-serif",
    fontSize: "16px",
  },
  // Help me write styles
  composeContainer: {
    padding: "24px",
    backgroundColor: "white",
    borderRadius: "16px",
    border: "2px solid transparent",
    backgroundImage: "linear-gradient(white, white), conic-gradient(from 0deg, #FFB500, #ffc42e, #FFB500, #e6a200, #FFB500)",
    backgroundOrigin: "border-box",
    backgroundClip: "padding-box, border-box",
    boxShadow: "0 4px 16px rgba(255, 181, 0, 0.1)",
    animation: "rotateBorder 8s linear infinite",
    fontFamily: "'Inter', 'DM Sans', sans-serif",
    margin: "16px",
  },
  promptContainer: {
    marginBottom: "24px",
  },
  promptInput: {
    marginBottom: "16px",
    fontFamily: "'Inter', 'DM Sans', sans-serif",
  },
  optionsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginBottom: "20px",
  },
  previewContainer: {
    marginTop: "24px",
    padding: "24px",
    backgroundColor: "white",
    borderRadius: "16px",
    border: "2px solid transparent",
    backgroundImage: "linear-gradient(white, white), conic-gradient(from 0deg, #FFB500, #ffc42e, #FFB500, #e6a200, #FFB500)",
    backgroundOrigin: "border-box",
    backgroundClip: "padding-box, border-box",
    boxShadow: "0 6px 20px rgba(255, 181, 0, 0.15)",
    animation: "rotateBorder 8s linear infinite",
  },
  previewSubject: {
    fontWeight: "600",
    color: "#0B111A",
    marginBottom: "16px",
    fontSize: "18px",
    fontFamily: "'Inter', 'DM Sans', sans-serif",
  },
  previewBody: {
    lineHeight: "1.6",
    color: "#374151",
    whiteSpace: "pre-wrap",
    fontFamily: "'Inter', 'DM Sans', sans-serif",
    fontSize: "14px",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    marginTop: "24px",
  },
});

const App = (props) => {
  const { title } = props;
  const styles = useStyles();
  
  // State management
  const [activeTab, setActiveTab] = React.useState("summary");
  const [emailContent, setEmailContent] = React.useState("");
  const [emailMode, setEmailMode] = React.useState("read"); // 'read' or 'compose'
  const [isLoading, setIsLoading] = React.useState(false);
  const [results, setResults] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [success, setSuccess] = React.useState(null);
  const [copiedIndex, setCopiedIndex] = React.useState(null);
  const [instructions, setInstructions] = React.useState("");
  const [isProcessingInstructions, setIsProcessingInstructions] = React.useState(false);
  const [loadingStep, setLoadingStep] = React.useState("");
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  
  // Caching to avoid repeated API calls
  const [summaryCache, setSummaryCache] = React.useState(null);
  const [rephraseCache, setRephraseCache] = React.useState(null);
  const [processedEmailContent, setProcessedEmailContent] = React.useState("");
  
  // Help me write states
  const [emailPrompt, setEmailPrompt] = React.useState("");
  const [emailTone, setEmailTone] = React.useState("professional");
  const [emailType, setEmailType] = React.useState("general");
  const [generatedEmail, setGeneratedEmail] = React.useState(null);
  const [isGenerating, setIsGenerating] = React.useState(false);

  React.useEffect(() => {
    initializeAPI();
    loadEmailContent();
  }, []);

  // Monitor email content changes
  React.useEffect(() => {
    if (emailContent && !isInitialLoading) {
      // Check if email content has changed - if so, clear caches
      if (emailContent !== processedEmailContent) {
        console.log('Email content changed, clearing cache');
        setSummaryCache(null);
        setRephraseCache(null);
        setProcessedEmailContent(emailContent);
        processEmailContent();
      }
    }
  }, [emailContent]);

  // Monitor tab changes separately
  React.useEffect(() => {
    if (emailContent && !isInitialLoading) {
      console.log('Tab changed to:', activeTab, 'Loading cached results');
      loadCachedResults();
    }
  }, [activeTab]);

  const loadCachedResults = () => {
    console.log('Loading cached results for tab:', activeTab);
    
    if (activeTab === "summary") {
      if (summaryCache) {
        console.log('Using cached summary results');
        setResults(summaryCache);
        setIsLoading(false);
      } else if (emailContent && processedEmailContent === emailContent) {
        console.log('No summary cache, processing content');
        processEmailContent();
      }
    } else if (activeTab === "rephrase") {
      if (rephraseCache) {
        console.log('Using cached rephrase results');
        setResults(rephraseCache);
        setIsLoading(false);
      } else if (emailContent && processedEmailContent === emailContent) {
        console.log('No rephrase cache, processing content');
        processEmailContent();
      }
    }
    // For 'help' tab, don't auto-process anything
  };

  const initializeAPI = async () => {
    try {
      console.log('Initializing API...');
      await streamlineAPI.initialize();
      console.log('API initialized successfully');
    } catch (err) {
      console.error("API initialization failed:", err);
    }
  };

  const loadEmailContent = async () => {
    try {
      setLoadingStep("📧 Accessing current email...");
      console.log('Loading email content...');
      
      // Small delay to show the step
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setLoadingStep("📖 Reading email content...");
      const email = await emailProcessor.getCurrentEmail();
      console.log('Email content loaded:', email);
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (email) {
        setLoadingStep("✨ Email loaded successfully!");
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Set email mode
        setEmailMode(email.mode || 'read');
        console.log('Email mode detected:', email.mode);
        
        if (email.content && email.content !== emailContent) {
          console.log('Setting new email content');
          setEmailContent(email.content);
        } else {
          console.log('Email content unchanged, skipping state update');
        }
        setIsInitialLoading(false);
      } else {
        throw new Error('No email content found');
      }
    } catch (err) {
      console.error('Error loading email content:', err);
      setError('Unable to load email content. Please try again.');
      setIsInitialLoading(false);
    }
  };

  const handleTabSelect = (event, data) => {
    setActiveTab(data.value);
    setResults([]);
    setError(null);
    setSuccess(null);
    setInstructions("");
  };

  const processEmailContent = async () => {
    if (!emailContent.trim()) return;

    // Check cache first
    if (activeTab === "summary" && summaryCache) {
      setResults(summaryCache);
      return;
    } else if (activeTab === "rephrase" && rephraseCache) {
      setResults(rephraseCache);
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults([]);

    try {
      if (activeTab === "summary") {
        setLoadingStep("🧠 Analyzing email structure...");
        await new Promise(resolve => setTimeout(resolve, 800));
        
        setLoadingStep("📝 Generating intelligent summaries...");
        console.log('Calling generateEmailSummaries');
        const apiResults = await streamlineAPI.generateEmailSummaries(emailContent);
        
        setLoadingStep("✨ Polishing your summaries...");
        await new Promise(resolve => setTimeout(resolve, 400));
        
        console.log('API results:', apiResults);
        setResults(apiResults);
        setSummaryCache(apiResults); // Cache the results
        setSuccess(`Generated ${apiResults.length} intelligent summaries!`);
      } else {
        setLoadingStep("🎯 Understanding email tone...");
        await new Promise(resolve => setTimeout(resolve, 800));
        
        setLoadingStep("✍️ Crafting rephrased versions...");
        console.log('Calling generateEmailRephrases');
        const apiResults = await streamlineAPI.generateEmailRephrases(emailContent);
        
        setLoadingStep("🎨 Adding final touches...");
        await new Promise(resolve => setTimeout(resolve, 400));
        
        console.log('API results:', apiResults);
        setResults(apiResults);
        setRephraseCache(apiResults); // Cache the results
        setSuccess(`Generated ${apiResults.length} expertly rephrased versions!`);
      }
    } catch (err) {
      console.error('Error processing email content:', err);
      setError(`Failed to ${activeTab}: ${err.message}`);
    } finally {
      console.log('Setting loading state to false');
      setIsLoading(false);
      setLoadingStep("");
    }
  };

  const processWithInstructions = async () => {
    if (!emailContent.trim() || !instructions.trim()) return;

    setIsProcessingInstructions(true);
    setError(null);

    try {
      setLoadingStep("🎯 Understanding your instructions...");
      await new Promise(resolve => setTimeout(resolve, 600));
      
      const instructionPrompt = `${emailContent}\n\nAdditional instructions: ${instructions}`;
      
      if (activeTab === "summary") {
        setLoadingStep("📊 Customizing summaries...");
        const apiResults = await streamlineAPI.generateEmailSummaries(instructionPrompt);
        setLoadingStep("✨ Your personalized summaries are ready!");
        await new Promise(resolve => setTimeout(resolve, 300));
        setResults(apiResults);
        setSuccess(`Updated summaries with your custom instructions!`);
      } else {
        setLoadingStep("✍️ Refining rephrases...");
        const apiResults = await streamlineAPI.generateEmailRephrases(instructionPrompt);
        setLoadingStep("🎨 Your tailored rephrases are ready!");
        await new Promise(resolve => setTimeout(resolve, 300));
        setResults(apiResults);
        setSuccess(`Updated rephrases with your custom instructions!`);
      }
      
      setInstructions("");
    } catch (err) {
      console.error('Error processing with instructions:', err);
      setError(`Failed to process instructions: ${err.message}`);
    } finally {
      setIsProcessingInstructions(false);
      setLoadingStep("");
    }
  };

  const copyToClipboard = async (content, index) => {
    try {
      // Clean the content from markdown formatting for clipboard
      const cleanText = content
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markers
        .replace(/\*(.*?)\*/g, '$1') // Remove italic markers
        .replace(/`([^`]+)`/g, '$1') // Remove code markers
        .replace(/#{1,6}\s*(.+)/g, '$1') // Remove heading markers
        .trim();
        
      await navigator.clipboard.writeText(cleanText);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      setError("Failed to copy to clipboard");
    }
  };

  const insertResultContent = async (result, index) => {
    try {
      setLoadingStep("📝 Inserting content...");
      
      // Convert markdown to HTML for proper insertion
      let htmlContent = result.content
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
        .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
        .replace(/\n/g, '<br>'); // Line breaks
      
      await emailProcessor.insertEmailContent(htmlContent, true);
      setSuccess(`Inserted ${result.style || result.tone} version into email!`);
    } catch (err) {
      console.error('Failed to insert content:', err);
      setError(`Failed to insert: ${err.message}`);
    } finally {
      setLoadingStep("");
    }
  };

  const renderFormattedContent = (content, keywords = []) => {
    if (!content) return null;
    
    // Clean up any markdown artifacts and normalize content
    let cleanContent = content
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/`([^`]+)`/g, '$1') // Remove inline code backticks
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Convert links to plain text
      .replace(/#{1,6}\s*(.+)/g, '$1') // Remove heading markers
      .trim();
    
    // Split content into paragraphs and bullet points
    const lines = cleanContent.split('\n').filter(line => line.trim());
    const elements = [];
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      // Skip empty lines
      if (!trimmed) return;
      
      // Handle bullet points (-, •, *, or ·)
      if (/^[-•*·]\s/.test(trimmed)) {
        const bulletText = trimmed.replace(/^[-•*·]\s*/, '');
        elements.push(
          <div key={`bullet-${index}`} className={styles.bulletPoint}>
            <div className={styles.bulletIcon}></div>
            <Text className={styles.contentText}>
              {formatInlineText(bulletText, keywords)}
            </Text>
          </div>
        );
      }
      // Handle numbered lists (1., 2., etc.)
      else if (/^\d+\.\s/.test(trimmed)) {
        const numberMatch = trimmed.match(/^(\d+)\.\s*(.+)/);
        if (numberMatch) {
          const [, number, listText] = numberMatch;
          elements.push(
            <div key={`numbered-${index}`} className={styles.bulletPoint}>
              <Text weight="semibold" style={{ color: "#3b82f6", minWidth: "24px", fontSize: "14px" }}>
                {number}.
              </Text>
              <Text className={styles.contentText}>
                {formatInlineText(listText, keywords)}
              </Text>
            </div>
          );
        }
      }
      // Handle headings (convert remaining # to bold)
      else if (trimmed.startsWith('#')) {
        const headingText = trimmed.replace(/^#+\s*/, '');
        elements.push(
          <Text key={`heading-${index}`} 
                weight="bold" 
                size={500}
                style={{ 
                  marginBottom: "8px", 
                  marginTop: index > 0 ? "16px" : "0",
                  display: "block",
                  color: "#1f2937"
                }}>
            {formatInlineText(headingText, keywords)}
          </Text>
        );
      }
      // Regular paragraphs
      else {
        elements.push(
          <Text key={`para-${index}`} 
                className={styles.contentText} 
                style={{ 
                  marginBottom: "12px", 
                  display: "block",
                  lineHeight: "1.6"
                }}>
            {formatInlineText(trimmed, keywords)}
          </Text>
        );
      }
    });
    
    return elements.length > 0 ? elements : (
      <Text className={styles.contentText}>
        {formatInlineText(cleanContent, keywords)}
      </Text>
    );
  };

  const formatInlineText = (text, keywords = []) => {
    if (!text || typeof text !== 'string') return text;
    
    // Use keywords from API if available, otherwise fallback to default keywords
    const importantKeywords = keywords && keywords.length > 0 ? keywords : [
      'action required', 'urgent', 'deadline', 'meeting', 'scheduled', 'approve', 'approval',
      'budget', 'revenue', 'profit', 'loss', 'increase', 'decrease', 'target', 'goal',
      'project', 'complete', 'finished', 'pending', 'delayed', 'priority', 'critical',
      'decision', 'recommend', 'suggest', 'propose', 'conclusion', 'result', 'outcome'
    ];
    
    let processedText = text;
    let keyCounter = 0;
    const parts = [];
    
    // Step 1: Handle bold formatting **text**
    const boldRegex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;
    
    while ((match = boldRegex.exec(processedText)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        const beforeText = processedText.slice(lastIndex, match.index);
        parts.push(highlightKeywords(beforeText, importantKeywords, keyCounter));
        keyCounter += 10;
      }
      
      // Add the bold element with highlighting if it contains keywords
      const boldContent = match[1];
      const isKeyword = importantKeywords.some(keyword => 
        boldContent.toLowerCase().includes(keyword.toLowerCase())
      );
      
      parts.push(
        <strong 
          key={`bold-${keyCounter++}`}
          className={isKeyword ? 'highlight' : ''}
          style={isKeyword ? {
            backgroundColor: "#fff3cd",
            color: "#0B111A",
            fontWeight: "600",
            padding: "2px 4px",
            borderRadius: "3px"
          } : {}}
        >
          {boldContent}
        </strong>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < processedText.length) {
      const remainingText = processedText.slice(lastIndex);
      parts.push(highlightKeywords(remainingText, importantKeywords, keyCounter));
    }
    
    return parts.length > 1 ? parts : highlightKeywords(processedText, importantKeywords, 0);
  };

  const highlightKeywords = (text, keywords, startKey) => {
    if (!text) return text;
    
    let result = [text];
    let keyCounter = startKey;
    
    // Helper function to escape special regex characters
    const escapeRegex = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };
    
    keywords.forEach(keyword => {
      try {
        // Clean and escape the keyword
        const cleanKeyword = keyword.trim().replace(/^\*+|\*+$/g, ''); // Remove leading/trailing asterisks
        if (!cleanKeyword) return; // Skip empty keywords
        
        const escapedKeyword = escapeRegex(cleanKeyword);
        const regex = new RegExp(`\\b(${escapedKeyword})\\b`, 'gi');
      const newResult = [];
      
      result.forEach(part => {
        if (typeof part === 'string') {
          const splits = part.split(regex);
          for (let i = 0; i < splits.length; i++) {
            if (i % 2 === 0) {
              // Regular text
              if (splits[i]) newResult.push(splits[i]);
            } else {
              // Keyword match
              newResult.push(
                <span 
                  key={`highlight-${keyCounter++}`}
                  style={{
                    backgroundColor: "#fff3cd",
                    color: "#0B111A",
                    fontWeight: "600",
                    padding: "1px 3px",
                    borderRadius: "3px"
                  }}
                >
                  {splits[i]}
                </span>
              );
            }
          }
        } else {
          newResult.push(part);
        }
      });
      
      result = newResult;
      } catch (error) {
        console.warn(`Failed to highlight keyword "${keyword}":`, error);
        // Skip this keyword and continue with others
      }
    });
    
    return result.length === 1 ? result[0] : result;
  };

  // Help me write functions
  const generateEmailFromPrompt = async () => {
    if (!emailPrompt.trim()) return;

    setIsGenerating(true);
    setError(null);
    setLoadingStep("🤖 Understanding your request...");

    try {
      await new Promise(resolve => setTimeout(resolve, 600));
      setLoadingStep("✍️ Crafting your email...");
      
      const generatedEmailData = await streamlineAPI.generateEmailFromPrompt(emailPrompt, {
        tone: emailTone,
        emailType: emailType
      });
      
      setLoadingStep("✨ Your email is ready!");
      await new Promise(resolve => setTimeout(resolve, 400));
      
      setGeneratedEmail(generatedEmailData);
      setSuccess("Email generated successfully!");
    } catch (err) {
      console.error('Error generating email:', err);
      setError(`Failed to generate email: ${err.message}`);
    } finally {
      setIsGenerating(false);
      setLoadingStep("");
    }
  };

  const insertGeneratedEmail = async () => {
    if (!generatedEmail) return;

    try {
      setLoadingStep("📝 Inserting email into compose window...");
      
      // Format email for insertion (with HTML formatting)
      const formattedEmail = `<p>${generatedEmail.body.replace(/\n/g, '</p><p>')}</p>`;
      
      await emailProcessor.insertEmailContent(formattedEmail, true);
      
      // Also set the subject if available
      if (generatedEmail.subject) {
        try {
          Office.context.mailbox.item.subject.setAsync(generatedEmail.subject);
        } catch (subjectError) {
          console.warn('Could not set subject:', subjectError);
        }
      }
      
      setSuccess("Email inserted successfully! You can now review and send it.");
      setGeneratedEmail(null);
      setEmailPrompt("");
    } catch (err) {
      console.error('Error inserting email:', err);
      setError(`Failed to insert email: ${err.message}`);
    } finally {
      setLoadingStep("");
    }
  };

  const regenerateEmail = () => {
    setGeneratedEmail(null);
    generateEmailFromPrompt();
  };

  const renderShimmerCards = () => {
    return (
      <div className={styles.shimmerContainer}>
        {[1, 2, 3].map((index) => (
          <div key={index} className={styles.shimmerCard}>
            <div className={styles.shimmerHeader}>
              <Skeleton>
                <SkeletonItem shape="rectangle" size={16} style={{ width: "100px", height: "24px", borderRadius: "6px" }} />
              </Skeleton>
              <Skeleton>
                <SkeletonItem shape="rectangle" size={16} style={{ width: "60px", height: "32px", borderRadius: "8px" }} />
              </Skeleton>
            </div>
            <Skeleton>
              <SkeletonItem shape="rectangle" size={16} style={{ width: "100%", height: "16px", marginBottom: "12px", borderRadius: "4px" }} />
              <SkeletonItem shape="rectangle" size={16} style={{ width: "95%", height: "16px", marginBottom: "12px", borderRadius: "4px" }} />
              <SkeletonItem shape="rectangle" size={16} style={{ width: "88%", height: "16px", marginBottom: "16px", borderRadius: "4px" }} />
              <SkeletonItem shape="rectangle" size={16} style={{ width: "92%", height: "16px", borderRadius: "4px" }} />
            </Skeleton>
          </div>
        ))}
      </div>
    );
  };

  // Welcome screen when no text is selected
  if (isInitialLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingContainer}>
          <Spinner label="Initializing Streamline..." size="large" />
          <Text size={400} weight="semibold" style={{ color: "#0078d4", marginTop: "12px" }}>
            {loadingStep || "🚀 Getting ready..."}
          </Text>
          <Text size={300} style={{ color: "#6a737d", textAlign: "center", maxWidth: "300px", lineHeight: "1.4" }}>
            We're setting up your AI-powered email assistant. This will just take a moment!
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Error/Success Messages */}
      {error && (
        <MessageBar 
          intent="error" 
          style={{ 
            marginBottom: "16px", 
            fontFamily: "'Avenir', 'Avenir Next', 'Inter', sans-serif"
          }}
        >
          {error}
        </MessageBar>
      )}
      
      {success && (
        <MessageBar 
          intent="success" 
          style={{ 
            marginBottom: "16px", 
            fontFamily: "Avenir, sans-serif"
          }}
        >
          {success}
        </MessageBar>
      )}

      {/* Show different UI based on email mode */}
      {emailMode === 'compose' ? (
        // COMPOSE MODE: Help me write interface
        <div className={styles.composeContainer}>
          <Text size={500} weight="semibold" style={{ 
            marginBottom: "8px", 
            display: "flex", 
            alignItems: "center", 
            gap: "8px",
            fontFamily: "Avenir, sans-serif",
            color: "#374151"
          }}>
            <Mail24Regular style={{ color: "#6b7280" }} />
            Help me write
          </Text>
          <Text size={300} style={{ 
            color: "#6b7280", 
            marginBottom: "24px",
            fontFamily: "Avenir, sans-serif"
          }}>
            Describe what you want to write about, and I'll generate a professional email for you.
          </Text>

          <div className={styles.promptContainer}>
            <Field label="What do you want to write about?" style={{ fontFamily: "Avenir, sans-serif" }}>
              <Textarea
                value={emailPrompt}
                onChange={(e) => setEmailPrompt(e.target.value)}
                placeholder="e.g., Schedule a meeting with the marketing team to discuss Q1 strategy"
                rows={3}
                style={{ 
                  borderRadius: "8px",
                  fontFamily: "Avenir, sans-serif",
                  fontSize: "14px"
                }}
              />
            </Field>
          </div>

          <div className={styles.optionsGrid}>
            <Field label="Tone" style={{ fontFamily: "Avenir, sans-serif" }}>
              <Dropdown
                value={emailTone}
                onOptionSelect={(e, data) => setEmailTone(data.optionValue)}
                style={{
                  fontFamily: "Avenir, sans-serif"
                }}
              >
                <Option value="professional">Professional</Option>
                <Option value="friendly">Friendly</Option>
                <Option value="formal">Formal</Option>
                <Option value="casual">Casual</Option>
              </Dropdown>
            </Field>
            
            <Field label="Email Type" style={{ fontFamily: "Avenir, sans-serif" }}>
              <Dropdown
                value={emailType}
                onOptionSelect={(e, data) => setEmailType(data.optionValue)}
                style={{
                  fontFamily: "Avenir, sans-serif"
                }}
              >
                <Option value="general">General</Option>
                <Option value="meeting_request">Meeting Request</Option>
                <Option value="follow_up">Follow Up</Option>
                <Option value="introduction">Introduction</Option>
                <Option value="thank_you">Thank You</Option>
                <Option value="complaint">Complaint</Option>
              </Dropdown>
            </Field>
          </div>

          <Button
            appearance="primary"
            icon={<Add24Regular />}
            onClick={generateEmailFromPrompt}
            disabled={!emailPrompt.trim() || isGenerating}
            style={{ 
              width: "100%", 
              marginBottom: "16px",
              fontFamily: "'Inter', 'DM Sans', sans-serif",
              fontWeight: "600"
            }}
          >
            {isGenerating ? "Generating Email..." : "Generate Email"}
          </Button>

          {/* Loading State for Email Generation */}
          {isGenerating && (
            <div className={styles.loadingContainer}>
              <Spinner label="AI is writing your email..." size="large" />
              <Text size={400} weight="semibold" style={{ color: "#6b7280", marginTop: "12px", fontFamily: "Avenir, sans-serif" }}>
                {loadingStep || "🤖 AI is working..."}
              </Text>
              <Text size={300} style={{ color: "#9ca3af", textAlign: "center", maxWidth: "350px", lineHeight: "1.4", fontFamily: "Avenir, sans-serif" }}>
                Creating a professional email tailored to your needs...
              </Text>
            </div>
          )}

          {/* Email Preview */}
          {generatedEmail && !isGenerating && (
            <div className={styles.previewContainer}>
              <Text className={styles.previewSubject}>
                Subject: {generatedEmail.subject}
              </Text>
              <div className={styles.previewBody}>
                {renderFormattedContent(generatedEmail.body)}
              </div>
              
              <div className={styles.actionButtons}>
                <Button
                  appearance="primary"
                  icon={<Mail24Regular />}
                  onClick={insertGeneratedEmail}
                  style={{
                    fontFamily: "Avenir, sans-serif",
                    fontWeight: "600"
                  }}
                >
                  Insert into Email
                </Button>
                <Button
                  appearance="secondary"
                  onClick={regenerateEmail}
                  style={{
                    fontFamily: "Avenir, sans-serif",
                    fontWeight: "500"
                  }}
                >
                  Regenerate
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        // READ MODE: Summary and Rephrase interface (existing code)
        <>
          {/* Tab Selection */}
          <div className={styles.tabContainer}>
            <TabList 
              selectedValue={activeTab} 
              onTabSelect={handleTabSelect}
              style={{
                fontFamily: "Avenir, sans-serif"
              }}
            >
              <Tab 
                id="summary" 
                value="summary"
                style={{
                  fontFamily: "Avenir, sans-serif",
                  fontWeight: activeTab === "summary" ? "600" : "500",
                  color: activeTab === "summary" ? "#374151" : "#6b7280"
                }}
              >
                <DocumentText24Regular />
                Summary
              </Tab>
              <Tab 
                id="rephrase" 
                value="rephrase"
                style={{
                  fontFamily: "Avenir, sans-serif",
                  fontWeight: activeTab === "rephrase" ? "600" : "500",
                  color: activeTab === "rephrase" ? "#374151" : "#6b7280"
                }}
              >
                <Edit24Regular />
                Rephrase
              </Tab>
            </TabList>
          </div>

          {/* Loading State with Exciting Progress */}
          {(isLoading || isProcessingInstructions) && (
            <div>
              <div className={styles.loadingContainer}>
                <Spinner label="AI Processing..." size="large" />
                <Text size={400} weight="semibold" style={{ color: "#6b7280", marginTop: "12px", fontFamily: "Avenir, sans-serif" }}>
                  {loadingStep || "🤖 AI is working..."}
                </Text>
                <Text size={300} style={{ color: "#9ca3af", textAlign: "center", maxWidth: "350px", lineHeight: "1.4", fontFamily: "Avenir, sans-serif" }}>
                  {activeTab === "summary" 
                    ? "Creating intelligent summaries that capture the key points..."
                    : "Crafting professional rephrases with perfect tone and clarity..."
                  }
                </Text>
              </div>
              {renderShimmerCards()}
            </div>
          )}

          {/* Results with Beautiful Custom Styling */}
          {results.length > 0 && !isLoading && (
            <div className={`${styles.resultsContainer} ${styles.fadeIn}`}>
              {results.map((result, index) => (
                <div 
                  key={index} 
                  className={`${styles.resultCard} ${styles.fadeIn} streamline-card ${index === 0 ? 'featured' : ''}`} 
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={styles.resultHeader}>
                    <div className={styles.resultTitle}>
                      <div className={styles.resultBadge}>
                        {result.style || result.tone} {activeTab === "summary" ? "Summary" : "Version"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {/* Insert button for compose mode */}
                      {emailMode === "compose" && activeTab === "rephrase" && (
                        <Button
                          size="small"
                          appearance="primary"
                          icon={<Mail24Regular />}
                          onClick={() => insertResultContent(result, index)}
                          style={{ 
                            fontSize: "11px", 
                            minWidth: "70px",
                            backgroundColor: "#FFB500",
                            borderColor: "#FFB500",
                            color: "#0B111A",
                            fontFamily: "'Avenir', 'Avenir Next', 'Inter', sans-serif",
                            fontWeight: "700",
                            borderRadius: "15px",
                            height: "28px"
                          }}
                        >
                          Insert
                        </Button>
                      )}
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={copiedIndex === index ? <CheckmarkCircle24Regular className={styles.successIcon} /> : <Copy24Regular />}
                        onClick={() => copyToClipboard(result.content, index)}
                        className={styles.copyButton}
                      >
                        {copiedIndex === index ? "Copied!" : "Copy"}
                      </Button>
                    </div>
                  </div>
                  <div className={styles.resultContent}>
                    {renderFormattedContent(result.content, result.keywords)}
                  </div>
                </div>
              ))}
              
              {/* Enhanced Instructions Input */}
              <div className={styles.instructionsContainer}>
                <Text className={styles.instructionsLabel}>
                  🎯 Refine your {activeTab === "summary" ? "summary" : "rephrase"}
                </Text>
                <Field>
                  <Input
                    className={styles.instructionsInput}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder={`Add specific instructions to improve the ${activeTab}...`}
                    multiline
                    rows={3}
                    style={{ 
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontFamily: "Avenir, sans-serif"
                    }}
                  />
                  <Button
                    appearance="primary"
                    icon={<Send24Regular />}
                    onClick={processWithInstructions}
                    disabled={!instructions.trim() || isProcessingInstructions}
                    style={{ 
                      width: "100%", 
                      fontFamily: "Avenir, sans-serif",
                      fontWeight: "600",
                      marginTop: "12px"
                    }}
                  >
                    {isProcessingInstructions ? "Processing..." : "Send Instructions"}
                  </Button>
                </Field>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

App.propTypes = {
  title: PropTypes.string,
};

export default App;